/**
 * patternShapesConstants.ts
 *
 * Shared constants for Pattern Shapes / Pattern Blocks manipulative.
 * Import from here rather than from PatternShapesManipulative.tsx so that
 * the popup, snap helpers, and any future utilities share a single source of truth.
 */
/* ------------------------------------------------------------------ */
/* GRID CONSTANTS */
/* ------------------------------------------------------------------ */
/** Canvas pixel size for one pattern-block unit (= 2 × BG_GRID). */
export var UNIT = 110;
/** Background triangular grid cell size in canvas pixels. */
export var BG_GRID = 55;
/**
 * Row height for the triangular grid (irrational number).
 * = BG_GRID × (√3 / 2) ≈ 47.63px
 */
export var TRI_H = (BG_GRID * Math.sqrt(3)) / 2;
/** Size for toolbar/button shape previews — NOT used on the canvas. */
export var BUTTON_UNIT = 60;
/* ------------------------------------------------------------------ */
/* DERIVED RHOMBUS_BLUE HELPERS (depend on UNIT) */
/* ------------------------------------------------------------------ */
/** Width of the blue rhombus = 1.5 × UNIT. */
export var RB_W = UNIT * 1.5;
/** Height of the blue rhombus = UNIT × 0.866 (equilateral triangle height). */
export var RB_H = UNIT * 0.866;
/** Horizontal slant offset of the blue rhombus = 0.5 × UNIT. */
export var RB_S = UNIT * 0.5;
/* ------------------------------------------------------------------ */
/* SVG PATH DEFINITIONS — CANVAS SIZE (UNIT-based) */
/* ------------------------------------------------------------------ */
export var SHAPE_PATHS = {
    triangle_equilateral: "M ".concat(UNIT / 2, " 0 L ").concat(UNIT, " ").concat(UNIT * 0.866, " L 0 ").concat(UNIT * 0.866, " Z"),
    triangle_right: "M 0 0 L 0 ".concat(UNIT, " L ").concat(UNIT, " ").concat(UNIT, " Z"),
    square: "M 0 0 H ".concat(UNIT, " V ").concat(UNIT, " H 0 Z"),
    rhombus_blue: "M ".concat(RB_S, " 0 L ").concat(RB_W, " 0 L ").concat(RB_W - RB_S, " ").concat(RB_H, " L 0 ").concat(RB_H, " Z"),
    // rhombus_white: thin diamond — 30°/150° angles, side = UNIT.
    // half-long-diagonal = UNIT×cos(15°), half-short-diagonal = UNIT×sin(15°)
    // → side length = UNIT×√(sin²15+cos²15) = UNIT ✓  (matches hexagon edge)
    rhombus_white: "M ".concat(UNIT / 2, " ").concat(UNIT * (0.5 - Math.cos(Math.PI / 12)), " L ").concat(UNIT * (0.5 + Math.sin(Math.PI / 12)), " ").concat(UNIT / 2, " L ").concat(UNIT / 2, " ").concat(UNIT * (0.5 + Math.cos(Math.PI / 12)), " L ").concat(UNIT * (0.5 - Math.sin(Math.PI / 12)), " ").concat(UNIT / 2, " Z"),
    // Trapezoid: top=UNIT, bottom=2×UNIT, height=UNIT×√3/2 (= UNIT×0.866, half of hexagon height)
    trapezoid: "M ".concat(UNIT * 0.5, " 0 H ").concat(UNIT * 1.5, " L ").concat(UNIT * 2, " ").concat(UNIT * 0.866, " H 0 Z"),
    // Hexagon: 4 cells wide × ~3.46 cells tall — regular hexagon, flat top/bottom
    hexagon: "M ".concat(UNIT / 2, " 0 H ").concat(UNIT * 1.5, " L ").concat(UNIT * 2, " ").concat(UNIT * 0.866, " L ").concat(UNIT * 1.5, " ").concat(UNIT * 1.732, " H ").concat(UNIT / 2, " L 0 ").concat(UNIT * 0.866, " Z"),
    outline: '',
};
/* ------------------------------------------------------------------ */
/* SVG PATH DEFINITIONS — BUTTON SIZE (BUTTON_UNIT-based) */
/* ------------------------------------------------------------------ */
export var SHAPE_PATHS_BUTTON = {
    triangle_equilateral: "M ".concat(BUTTON_UNIT / 2, " 0 L ").concat(BUTTON_UNIT, " ").concat(BUTTON_UNIT * 0.866, " L 0 ").concat(BUTTON_UNIT * 0.866, " Z"),
    triangle_right: "M 0 0 L 0 ".concat(BUTTON_UNIT, " L ").concat(BUTTON_UNIT, " ").concat(BUTTON_UNIT, " Z"),
    square: "M 0 0 H ".concat(BUTTON_UNIT, " V ").concat(BUTTON_UNIT, " H 0 Z"),
    rhombus_blue: "M 15 5 L 60 5 L 45 55 L 0 55 Z",
    rhombus_white: "M ".concat(BUTTON_UNIT / 2, " ").concat(BUTTON_UNIT * (0.5 - Math.cos(Math.PI / 12)), " L ").concat(BUTTON_UNIT * (0.5 + Math.sin(Math.PI / 12)), " ").concat(BUTTON_UNIT / 2, " L ").concat(BUTTON_UNIT / 2, " ").concat(BUTTON_UNIT * (0.5 + Math.cos(Math.PI / 12)), " L ").concat(BUTTON_UNIT * (0.5 - Math.sin(Math.PI / 12)), " ").concat(BUTTON_UNIT / 2, " Z"),
    trapezoid: "M ".concat(BUTTON_UNIT * 0.25, " 0 H ").concat(BUTTON_UNIT * 0.75, " L ").concat(BUTTON_UNIT, " ").concat(BUTTON_UNIT * 0.433, " H 0 Z"),
    hexagon: "M ".concat(BUTTON_UNIT * 0.25, " ").concat(BUTTON_UNIT * 0.067, " H ").concat(BUTTON_UNIT * 0.75, " L ").concat(BUTTON_UNIT, " ").concat(BUTTON_UNIT * 0.5, " L ").concat(BUTTON_UNIT * 0.75, " ").concat(BUTTON_UNIT * 0.933, " H ").concat(BUTTON_UNIT * 0.25, " L 0 ").concat(BUTTON_UNIT * 0.5, " Z"),
    outline: '',
};
/* ------------------------------------------------------------------ */
/* BOUNDING BOXES — CANVAS SIZE */
/* ------------------------------------------------------------------ */
/** Actual bounding box for each shape (minX, minY, width, height) in UNIT coordinates.
 *  Used for SVG viewBox, wrapper sizing, transform-origin, and rotate handle positioning. */
export var SHAPE_BOUNDS = {
    triangle_equilateral: { x: 0, y: 0, w: UNIT, h: UNIT * 0.866 },
    triangle_right: { x: 0, y: 0, w: UNIT, h: UNIT },
    square: { x: 0, y: 0, w: UNIT, h: UNIT },
    rhombus_blue: { x: 0, y: 0, w: RB_W, h: RB_H },
    rhombus_white: { x: UNIT * (0.5 - Math.sin(Math.PI / 12)), y: UNIT * (0.5 - Math.cos(Math.PI / 12)), w: UNIT * 2 * Math.sin(Math.PI / 12), h: UNIT * 2 * Math.cos(Math.PI / 12) },
    trapezoid: { x: 0, y: 0, w: UNIT * 2, h: UNIT * 0.866 },
    hexagon: { x: 0, y: 0, w: UNIT * 2, h: UNIT * 1.732 },
    outline: { x: 0, y: 0, w: UNIT, h: UNIT },
};
/* ------------------------------------------------------------------ */
/* SHAPE VERTICES — for vertex-to-vertex snapping */
/* ------------------------------------------------------------------ */
/** Local-coordinate vertices for each canvas shape type.
 *  Origin is the top-left corner of the UNIT bounding box.
 *  Edge midpoints are computed dynamically by getSnapPoints() for coverage. */
export var SHAPE_VERTICES = {
    triangle_equilateral: [
        { x: UNIT / 2, y: 0 },
        { x: UNIT, y: UNIT * 0.866 },
        { x: 0, y: UNIT * 0.866 },
    ],
    triangle_right: [
        { x: 0, y: 0 },
        { x: 0, y: UNIT },
        { x: UNIT, y: UNIT },
    ],
    square: [
        { x: 0, y: 0 },
        { x: UNIT, y: 0 },
        { x: UNIT, y: UNIT },
        { x: 0, y: UNIT },
    ],
    rhombus_blue: [
        { x: RB_S, y: 0 },
        { x: RB_W, y: 0 },
        { x: RB_W - RB_S, y: RB_H },
        { x: 0, y: RB_H },
    ],
    rhombus_white: [
        { x: UNIT / 2, y: UNIT * (0.5 - Math.cos(Math.PI / 12)) },
        { x: UNIT * (0.5 + Math.sin(Math.PI / 12)), y: UNIT / 2 },
        { x: UNIT / 2, y: UNIT * (0.5 + Math.cos(Math.PI / 12)) },
        { x: UNIT * (0.5 - Math.sin(Math.PI / 12)), y: UNIT / 2 },
    ],
    trapezoid: [
        { x: UNIT * 0.5, y: 0 },
        { x: UNIT * 1.5, y: 0 },
        { x: UNIT * 2, y: UNIT * 0.866 },
        { x: 0, y: UNIT * 0.866 },
    ],
    hexagon: [
        { x: UNIT / 2, y: 0 },
        { x: UNIT * 1.5, y: 0 },
        { x: UNIT * 2, y: UNIT * 0.866 },
        { x: UNIT * 1.5, y: UNIT * 1.732 },
        { x: UNIT / 2, y: UNIT * 1.732 },
        { x: 0, y: UNIT * 0.866 },
    ],
    outline: [],
};
/* ------------------------------------------------------------------ */
/* OUTLINE SHAPE VERTICES — for border-only snapping                   */
/* ------------------------------------------------------------------ */
/**
 * Polygon vertices for each of the 21 outline SVG templates, in SVG local coordinates
 * (matching the original SVG viewBox — no scaling applied).
 *
 * The outline snap pass transforms these into canvas coordinates using the outline's
 * `center` position (top-left origin) at the time of drop, then snaps shape vertices
 * to the nearest outline vertex within SNAP_THRESHOLD. This ensures shapes only snap
 * near the actual perimeter of the outline, not in the interior.
 *
 * Shapes that have path-based (non-polygon) SVGs or extremely complex traced polygons
 * are represented as empty arrays — they fall back to grid snapping inside outlines.
 *
 * Source: `public/assets/images/manipulative/patternShape/Shape_NN.svg`
 */
export var OUTLINE_VERTICES = {
    // Shape_01 — single large equilateral triangle (769.99 × 666.83)
    1: [
        { x: 384.99, y: 0 },
        { x: 0, y: 666.83 },
        { x: 769.99, y: 666.83 },
    ],
    // Shape_02 — large 18-point star/snowflake hexagon (550 × 571.56)
    2: [
        { x: 495, y: 285.78 }, { x: 550, y: 381.04 }, { x: 495, y: 476.3 },
        { x: 385, y: 476.3 }, { x: 330, y: 571.56 }, { x: 220, y: 571.56 },
        { x: 165, y: 476.3 }, { x: 55, y: 476.3 }, { x: 0, y: 381.04 },
        { x: 55, y: 285.78 }, { x: 0, y: 190.52 }, { x: 55, y: 95.26 },
        { x: 165, y: 95.26 }, { x: 220, y: 0 }, { x: 330, y: 0 },
        { x: 385, y: 95.26 }, { x: 495, y: 95.26 }, { x: 550, y: 190.52 },
    ],
    // Shape_03 — irregular pentagon (825 × 476.3)
    3: [
        { x: 275, y: 0 },
        { x: 27.5, y: 428.67 },
        { x: 0, y: 476.3 },
        { x: 550, y: 476.3 },
        { x: 825, y: 0 },
    ],
    // Shape_04 — irregular hexagon (660 × 572.35)
    4: [
        { x: 495, y: 0.79 },
        { x: 165.1, y: 0 },
        { x: 0, y: 286.57 },
        { x: 165, y: 572.35 },
        { x: 495, y: 572.35 },
        { x: 660, y: 286.57 },
    ],
    // Shape_05 — 4 nested equilateral triangles (907.5 × 714.45), multi-polygon
    // Vertices include all 4 triangle outlines merged; snap fires at any perimeter vertex
    5: [
        // Triangle 1 (smallest, top-left)
        { x: 55, y: 0 }, { x: 0, y: 95.26 }, { x: 110, y: 95.26 },
        // Triangle 2
        { x: 192.61, y: 47.63 }, { x: 82.61, y: 238.15 }, { x: 302.61, y: 238.15 },
        // Triangle 3
        { x: 385, y: 190.52 }, { x: 220, y: 476.3 }, { x: 550, y: 476.3 },
        // Triangle 4 (largest)
        { x: 687.5, y: 333.41 }, { x: 467.5, y: 714.45 }, { x: 907.5, y: 714.45 },
    ],
    // Shape_06 — 3 trapezoids stacked (880.23 × 571.56)
    6: [
        // Trapezoid 1 (top)
        { x: 55, y: 0 }, { x: 165, y: 0 }, { x: 110, y: 95.26 }, { x: 0, y: 95.26 },
        // Trapezoid 2 (middle)
        { x: 247.5, y: 142.89 }, { x: 467.5, y: 142.89 }, { x: 357.5, y: 333.41 }, { x: 137.5, y: 333.41 },
        // Trapezoid 3 (bottom)
        { x: 550.24, y: 285.78 }, { x: 880.23, y: 285.78 }, { x: 715.23, y: 571.56 }, { x: 385.23, y: 571.56 },
    ],
    // Shape_07 — 3 hexagons (742.43 × 571.5), each hexagon has 6 vertices
    7: [
        // Hexagon 1 (small, top-left)
        { x: 27.5, y: 0 }, { x: 0, y: 47.62 }, { x: 27.5, y: 95.25 },
        { x: 82.49, y: 95.25 }, { x: 109.99, y: 47.62 }, { x: 82.49, y: 0 },
        // Hexagon 2 (medium, center)
        { x: 357.48, y: 190.5 }, { x: 302.49, y: 95.25 }, { x: 192.51, y: 95.25 },
        { x: 137.51, y: 190.5 }, { x: 192.51, y: 285.75 }, { x: 302.49, y: 285.75 },
        // Hexagon 3 (large, bottom-right)
        { x: 632.45, y: 190.5 }, { x: 412.48, y: 190.5 }, { x: 302.49, y: 381 },
        { x: 412.48, y: 571.5 }, { x: 632.45, y: 571.5 }, { x: 742.43, y: 381 },
    ],
    // Shape_08 — 6 hexagons (990.28 × 476.45)
    8: [
        // Row 1, Hex 1
        { x: 219.97, y: 95.25 }, { x: 164.98, y: 0 }, { x: 54.99, y: 0 },
        { x: 0, y: 95.25 }, { x: 54.99, y: 190.5 }, { x: 164.98, y: 190.5 },
        // Row 1, Hex 2
        { x: 605.62, y: 96.25 }, { x: 550.63, y: 1 }, { x: 440.65, y: 1 },
        { x: 385.65, y: 96.25 }, { x: 440.65, y: 191.5 }, { x: 550.63, y: 191.5 },
        // Row 1, Hex 3
        { x: 990.28, y: 96.25 }, { x: 935.28, y: 1 }, { x: 825.3, y: 1 },
        { x: 770.31, y: 96.25 }, { x: 825.3, y: 191.5 }, { x: 935.28, y: 191.5 },
        // Row 2, Hex 1
        { x: 219.97, y: 381.2 }, { x: 164.98, y: 285.95 }, { x: 54.99, y: 285.95 },
        { x: 0, y: 381.2 }, { x: 54.99, y: 476.45 }, { x: 164.98, y: 476.45 },
        // Row 2, Hex 2
        { x: 605.62, y: 381.2 }, { x: 550.63, y: 285.95 }, { x: 440.65, y: 285.95 },
        { x: 385.65, y: 381.2 }, { x: 440.65, y: 476.45 }, { x: 550.63, y: 476.45 },
        // Row 2, Hex 3
        { x: 990.28, y: 381.2 }, { x: 935.28, y: 285.95 }, { x: 825.3, y: 285.95 },
        { x: 770.31, y: 381.2 }, { x: 825.3, y: 476.45 }, { x: 935.28, y: 476.45 },
    ],
    // Shape_09 — 3 large hexagons arranged diagonally (936 × 571.56)
    9: [
        // Hexagon 1 (top-left)
        { x: 110, y: 0 }, { x: 220, y: 0 }, { x: 330, y: 190.52 },
        { x: 275, y: 285.78 }, { x: 55, y: 285.78 }, { x: 0, y: 190.52 },
        // Hexagon 2 (top-right)
        { x: 716, y: 0 }, { x: 826, y: 0 }, { x: 936, y: 190.52 },
        { x: 881, y: 285.78 }, { x: 661, y: 285.78 }, { x: 606, y: 190.52 },
        // Hexagon 3 (bottom-center)
        { x: 440, y: 285.78 }, { x: 550, y: 285.78 }, { x: 660, y: 476.3 },
        { x: 605, y: 571.56 }, { x: 385, y: 571.56 }, { x: 330, y: 476.3 },
    ],
    // Shape_10 — staircase of 4 square groups (934 × 440)
    // Groups at: (0,330)→(110,440), (276,220)→(386,440), (549,110)→(659,440), (824,0)→(934,440)
    10: [
        { x: 0, y: 330 }, { x: 110, y: 330 }, { x: 110, y: 440 }, { x: 0, y: 440 },
        { x: 276, y: 220 }, { x: 386, y: 220 }, { x: 386, y: 440 }, { x: 276, y: 440 },
        { x: 549, y: 110 }, { x: 659, y: 110 }, { x: 659, y: 440 }, { x: 549, y: 440 },
        { x: 824, y: 0 }, { x: 934, y: 0 }, { x: 934, y: 440 }, { x: 824, y: 440 },
    ],
    // Shape_11 — 5 square groups in diagonal arrangement (825 × 715)
    // Groups: (0,110)→(110,220), (0,275)→(165,440), (0,495)→(220,715),
    //         (495,0)→(770,275), (495,385)→(825,715)
    11: [
        { x: 0, y: 110 }, { x: 110, y: 110 }, { x: 110, y: 220 }, { x: 0, y: 220 },
        { x: 0, y: 275 }, { x: 165, y: 275 }, { x: 165, y: 440 }, { x: 0, y: 440 },
        { x: 0, y: 495 }, { x: 220, y: 495 }, { x: 220, y: 715 }, { x: 0, y: 715 },
        { x: 495, y: 0 }, { x: 770, y: 0 }, { x: 770, y: 275 }, { x: 495, y: 275 },
        { x: 495, y: 385 }, { x: 825, y: 385 }, { x: 825, y: 715 }, { x: 495, y: 715 },
    ],
    // Shape_12 — 6 square groups in complex grid arrangement (933 × 660)
    // Groups: (0,0)→(440,220), (0,330)→(440,660), (550,0)→(660,440),
    //         (770,0)→(825,440), (550,550)→(770,660), (825,550)→(933,660)
    12: [
        { x: 0, y: 0 }, { x: 440, y: 0 }, { x: 440, y: 220 }, { x: 0, y: 220 },
        { x: 0, y: 330 }, { x: 440, y: 330 }, { x: 440, y: 660 }, { x: 0, y: 660 },
        { x: 550, y: 0 }, { x: 660, y: 0 }, { x: 660, y: 440 }, { x: 550, y: 440 },
        { x: 770, y: 0 }, { x: 825, y: 0 }, { x: 825, y: 440 }, { x: 770, y: 440 },
        { x: 550, y: 550 }, { x: 770, y: 550 }, { x: 770, y: 660 }, { x: 550, y: 660 },
        { x: 825, y: 550 }, { x: 933, y: 550 }, { x: 933, y: 660 }, { x: 825, y: 660 },
    ],
    // Shape_13 — 4 separate polygons (880 × 475.52): 2 triangles + 2 complex shapes
    13: [
        // Triangle (top-left): 3 vertices
        { x: 110, y: 0 }, { x: 0, y: 190.52 }, { x: 220, y: 190.52 },
        // Large top-right triangle group: 13 unique vertices (incl. bottom edge intermediates)
        { x: 770, y: 0 }, { x: 715, y: 95.26 }, { x: 660, y: 0 },
        { x: 605, y: 95.26 }, { x: 550, y: 0 }, { x: 495, y: 95.26 },
        { x: 440, y: 0 }, { x: 330, y: 190.52 },
        { x: 440, y: 190.52 }, { x: 550, y: 190.52 }, { x: 660, y: 190.52 },
        { x: 770, y: 190.52 }, { x: 880, y: 190.52 },
        // Bottom-left triangle group: 7 unique vertices (incl. bottom edge intermediates)
        { x: 220, y: 285 }, { x: 165, y: 380.26 }, { x: 110, y: 285 },
        { x: 0, y: 475.52 }, { x: 110, y: 475.52 }, { x: 220, y: 475.52 },
        { x: 330, y: 475.52 },
        // Bottom-right triangle group: 10 unique vertices (incl. bottom edge intermediates)
        { x: 770, y: 285 }, { x: 715, y: 380.26 }, { x: 660, y: 285 },
        { x: 605, y: 380.26 }, { x: 550, y: 285 }, { x: 440, y: 475.52 },
        { x: 550, y: 475.52 }, { x: 660, y: 475.52 }, { x: 770, y: 475.52 },
        { x: 880, y: 475.52 },
    ],
    // Shape_14 — complex single polygon (660 × 571.56), 20 unique vertices
    14: [
        { x: 0, y: 0 }, { x: 220, y: 0 }, { x: 275, y: 95.26 },
        { x: 330, y: 0 }, { x: 440, y: 0 }, { x: 495, y: 95.26 },
        { x: 440, y: 190.52 }, { x: 550, y: 190.52 }, { x: 660, y: 381.04 },
        { x: 550, y: 381.04 }, { x: 495, y: 285.78 }, { x: 330, y: 571.56 },
        { x: 220, y: 571.56 }, { x: 275, y: 476.3 }, { x: 165, y: 476.3 },
        { x: 110, y: 381.04 }, { x: 55, y: 476.3 }, { x: 0, y: 381.04 },
        { x: 165, y: 95.26 }, { x: 55, y: 95.26 },
    ],
    // Shape_15 — complex single polygon (495 × 666.8), 17 unique vertices
    15: [
        { x: 55, y: 0 }, { x: 0, y: 95.3 }, { x: 165, y: 381 },
        { x: 110, y: 476.3 }, { x: 220, y: 666.8 }, { x: 275, y: 571.6 },
        { x: 220, y: 476.3 }, { x: 275, y: 381 }, { x: 385, y: 571.6 },
        { x: 495, y: 571.6 }, { x: 330, y: 285.8 }, { x: 440, y: 285.8 },
        { x: 495, y: 190.5 }, { x: 275, y: 190.5 }, { x: 275, y: 80.4 },
        { x: 165, y: 80.4 }, { x: 165, y: 190.5 },
    ],
    // Shape_16 — hex-tile arrangement (822.25 × 506.49), key structural corners
    16: [
        { x: 287.75, y: 403.86 }, { x: 315.25, y: 356.23 },
        { x: 506.71, y: 246.03 }, { x: 561.56, y: 341.03 },
        { x: 616.56, y: 245.77 }, { x: 672.0, y: 150.27 },
        { x: 767.26, y: 95.27 }, { x: 822.25, y: 0 },
        { x: 712.25, y: 0 }, { x: 616.99, y: 55.0 },
        { x: 506.56, y: 55.26 }, { x: 396.57, y: 55.26 },
        { x: 451.12, y: 150.76 }, { x: 260.25, y: 260.96 },
        { x: 150.25, y: 261.0 }, { x: 55.02, y: 315.98 },
        { x: 0, y: 411.25 }, { x: 95.23, y: 356.27 },
        { x: 67.73, y: 403.86 }, { x: 177.72, y: 403.86 },
        { x: 82.46, y: 458.86 }, { x: 192.46, y: 458.86 },
    ],
    // Shape_17 — complex 17-point polygon (931.05 × 740.52)
    17: [
        { x: 550, y: 550 }, { x: 880, y: 550 }, { x: 770, y: 740.52 },
        { x: 110, y: 740.52 }, { x: 0, y: 550 }, { x: 110, y: 550 },
        { x: 165, y: 454.74 }, { x: 110, y: 359.48 }, { x: 165, y: 264.22 },
        { x: 275, y: 264.22 }, { x: 330, y: 359.48 }, { x: 275, y: 454.74 },
        { x: 330, y: 550 }, { x: 440, y: 550 }, { x: 440, y: 0 },
        { x: 550, y: 0 }, { x: 931.05, y: 220 }, { x: 550, y: 440 },
    ],
    // Shape_18 — hexagonal ring/frame (879.8 × 601.04), outer + inner perimeter corners
    18: [
        // Outer perimeter
        { x: 329.94, y: 0 }, { x: 549.92, y: 0 },
        { x: 659.84, y: 190.52 }, { x: 824.81, y: 190.52 }, { x: 879.8, y: 190.52 },
        { x: 879.8, y: 410.52 }, { x: 824.83, y: 505.78 },
        { x: 659.84, y: 601.04 }, { x: 110.0, y: 601.04 },
        { x: 55.0, y: 505.78 }, { x: 0, y: 410.52 }, { x: 0, y: 190.52 },
        { x: 55.0, y: 190.52 }, { x: 219.96, y: 190.52 },
        // Inner perimeter (ring opening)
        { x: 274.96, y: 95.26 }, { x: 604.92, y: 95.26 },
        { x: 659.84, y: 410.52 }, { x: 604.85, y: 505.78 },
        { x: 274.98, y: 505.78 }, { x: 219.99, y: 410.52 },
        { x: 220.0, y: 410.52 }, { x: 220.0, y: 190.52 },
    ],
    // Shape_19 — complex polygon (666.52 × 701.26), explicit polygon vertices
    19: [
        { x: 41.17, y: 56 }, { x: 135.43, y: 110 }, { x: 135.43, y: 0 },
        { x: 190.43, y: 95.26 }, { x: 190.43, y: 317.76 }, { x: 286.11, y: 372.26 },
        { x: 476.52, y: 261.76 }, { x: 477.04, y: 371.76 }, { x: 573.49, y: 316.25 },
        { x: 666.52, y: 369.97 }, { x: 478.52, y: 481.26 }, { x: 572.85, y: 534.27 },
        { x: 572.85, y: 644.26 }, { x: 286.94, y: 479.76 }, { x: 286.32, y: 701.26 },
        { x: 190.67, y: 646.26 }, { x: 189.67, y: 539.26 }, { x: 0.5, y: 648.26 },
        { x: 0.5, y: 538.83 }, { x: 95.38, y: 484.26 }, { x: 95.38, y: 263.84 },
        { x: 0, y: 318.84 }, { x: 1, y: 208.84 }, { x: 96.17, y: 152.26 },
    ],
    // Shape_20 — complex polygon (709.34 × 465.5), explicit polygon vertices
    20: [
        { x: 709.34, y: 410.5 }, { x: 599.34, y: 410.5 }, { x: 504.26, y: 355.5 },
        { x: 409.78, y: 300.5 }, { x: 519.08, y: 300.5 }, { x: 614.34, y: 245.5 },
        { x: 504.34, y: 245.5 }, { x: 409.78, y: 190.5 }, { x: 464.77, y: 95.25 },
        { x: 409.78, y: 0 }, { x: 299.79, y: 0 }, { x: 244.8, y: 95.25 },
        { x: 299.79, y: 190.5 }, { x: 205.0, y: 245.5 }, { x: 95.0, y: 245.5 },
        { x: 190.26, y: 300.5 }, { x: 205.08, y: 355.5 }, { x: 110.0, y: 410.5 },
        { x: 0, y: 410.5 }, { x: 95.26, y: 465.5 }, { x: 205.26, y: 465.5 },
        { x: 300.26, y: 410.5 }, { x: 409.08, y: 410.5 }, { x: 504.08, y: 465.5 },
        { x: 614.08, y: 465.5 },
    ],
    // Shape_21 — nested L-shaped square grid (770 × 770), all structural corners
    21: [
        // Outer L-shapes and key grid corners
        { x: 0, y: 0 }, { x: 330, y: 0 }, { x: 770, y: 0 },
        { x: 330, y: 110 }, { x: 660, y: 110 }, { x: 770, y: 110 },
        { x: 110, y: 110 }, { x: 165, y: 165 },
        { x: 165, y: 495 }, { x: 220, y: 495 }, { x: 275, y: 275 }, { x: 275, y: 495 },
        { x: 495, y: 165 }, { x: 495, y: 275 }, { x: 495, y: 495 }, { x: 495, y: 605 },
        { x: 605, y: 165 }, { x: 605, y: 495 }, { x: 605, y: 605 },
        { x: 0, y: 440 }, { x: 55, y: 440 }, { x: 110, y: 440 },
        { x: 330, y: 330 }, { x: 385, y: 330 }, { x: 440, y: 330 },
        { x: 330, y: 385 }, { x: 385, y: 385 }, { x: 440, y: 385 },
        { x: 330, y: 440 }, { x: 385, y: 440 }, { x: 440, y: 440 },
        { x: 165, y: 605 }, { x: 440, y: 605 }, { x: 550, y: 605 },
        { x: 110, y: 660 }, { x: 330, y: 660 }, { x: 660, y: 660 },
        { x: 0, y: 770 }, { x: 275, y: 770 }, { x: 330, y: 770 },
        { x: 715, y: 770 }, { x: 770, y: 770 },
        { x: 660, y: 330 }, { x: 715, y: 330 }, { x: 770, y: 330 },
    ],
};
/** SVG viewBox dimensions for each outline shape (width × height in SVG local units).
 *  These match the `viewBox` attribute in the corresponding Shape_NN.svg files and are
 *  stored separately from `svgW/svgH` on the manipulative (which are canvas pixels). */
export var OUTLINE_SVG_DIMS = {
    1: { w: 769.99, h: 666.83 },
    2: { w: 550, h: 571.56 },
    3: { w: 825, h: 476.3 },
    4: { w: 660, h: 572.35 },
    5: { w: 907.5, h: 714.45 },
    6: { w: 880.23, h: 571.56 },
    7: { w: 742.43, h: 571.5 },
    8: { w: 990.28, h: 476.45 },
    9: { w: 936, h: 571.56 },
    10: { w: 934, h: 440 },
    11: { w: 825, h: 715 },
    12: { w: 933, h: 660 },
    13: { w: 880, h: 475.52 },
    14: { w: 660, h: 571.56 },
    15: { w: 495, h: 666.8 },
    16: { w: 822.25, h: 506.49 },
    17: { w: 931.05, h: 740.52 },
    18: { w: 879.8, h: 601.04 },
    19: { w: 666.52, h: 701.26 },
    20: { w: 709.34, h: 465.5 },
    21: { w: 770, h: 770 },
};
/** Snapping threshold in canvas pixels — shared by vertex snap and outline vertex snap. */
export var SNAP_THRESHOLD = 30;
